import i18nFactory from '@yandex-int/i18n';
import { get } from 'lodash';
import memoize from 're-reselect';

import { VCardErrorMapper } from '../../../service/BannerEditErrors/VCardsErrors/VCardsErrors';
import {
    getBannerPriceDataToApi,
    mapPhoneToSerpPreviewData,
    mapWorkingPeriodsToSerpPreviewData,
    replaceTemplate
} from '../../../service/BannerHelpers/BannerHelpers';
import { parseNumber } from '../../../service/FormatHelpers/FormatHelpers';
import { UrlHelpers } from '../../../service/UrlHelpers';
import {
    BannerId,
    Callout,
    MessengerType,
    VcardData
} from '../../../typings/banners';
import {
    EditedBanner,
    EditedBannerBase,
    EditedBannerFullContacts,
    EditedBannerImage,
    EditedBannerTurbolanding,
    EditedBannerTypeCpcVideo,
    EditedBannerTypeCpmAudio,
    EditedBannerTypeCpmBanner,
    EditedBannerTypeCpmGeoproduct,
    EditedBannerTypeCpmIndoor,
    EditedBannerTypeCpmOutdoor,
    EditedBannerTypeCpmVideo,
    EditedBannerTypeImage,
    EditedBannerTypeMcBanner,
    EditedBannerTypePerformance,
    EditedBannerTypeText,
    EditedBannerVCard,
    EditedBannerVideo,
    EditedCpmBanner,
    getEditedBannerWithCallouts,
    getEditedBannerWithFullContacts,
    getEditedBannerWithImage,
    getEditedBannerWithSitelinks,
    getEditedBannerWithTitleAndBody,
    getEditedBannerWithVcard,
    SubwayStationInfoForPreviewData
} from '../../../typings/bannersEditState';
import {
    AccessRights,
    CommonData,
    ValidationErrorData,
    ValidationResultItem
} from '../../../typings/common';
import {
    AdvType,
    AdvTypeClient,
    BannerImageForEdit,
    BannerImageType,
    ContactsEditorType
} from '../../../typings/domain';
import {
    GdAddAdInput,
    GdAddAdsInput,
    GdAddCpmAdInput,
    GdAddCpmAdsInput,
    GdAddInternalAdsInput,
    GdAddInternalAdsItemInput,
    GdAddMcBannerAdInput,
    GdAddMcBannerAdsInput,
    GdAddSmartAdsInput,
    GdAddVcardInput,
    GdAdPriceInput,
    GdAdsMassActionInput,
    GdInstantMessengerTypeInput,
    GdUpdateAdInput,
    GdUpdateAdsInput,
    GdUpdateCpmAdInput,
    GdUpdateCpmAdsInput,
    GdUpdateInternalAdsInput,
    GdUpdateInternalAdsItemInput,
    GdUpdateMcBannerAdInput,
    GdUpdateMcBannerAdsInput,
    GdUpdateSmartAdsInput
} from '../../../typings/graphql-grid';
import { EditedInternalBanner } from '../../../typings/internalBannersEditState';
import { OrganizationData } from '../../../typings/organizations';
import { PreviewData } from '../../../typings/preview';
import { ImageEditorsState, ImageEditorState } from '../../../typings/state';
import { Omit } from '../../../typings/utils';
import { VisitingCardEditorState } from '../../../typings/VisitingCardEditor';
import {
    mapMeasurersToApiFormat,
    mapPixelsToApiFormat,
    mapStringToGdAdvTypeInput
} from '../../banner/banner';

import * as keyset from './state-data-mappers.i18n';

const i18n = i18nFactory(keyset);

type TurbolandingApiFormat =
    | { turbolandingId: string; turbolandingHrefParams?: string }
    | undefined;

export const mapTurbolandingDataToAddApiFormat = (
    turbolanding?: EditedBannerTurbolanding
): TurbolandingApiFormat => {
    if (turbolanding && turbolanding.page) {
        return {
            turbolandingId: turbolanding.page.landingId,
            turbolandingHrefParams: turbolanding!.hrefParams
                ? turbolanding!.hrefParams
                : undefined
        };
    } else {
        return undefined;
    }
};

export const mapImageDataToApiFormat = (
    image?: EditedBannerImage
): {
    creativeId?: GraphQlLong | null;
    imageCreativeHash?: string | null;
    textBannerImageHash?: string | null;
} => {
    let textBannerImageHash, imageCreativeHash, creativeId;

    const imageData = (image && (image.data as BannerImageForEdit)) || null;

    if (imageData) {
        if (imageData.type && imageData.type === BannerImageType.TEXT) {
            textBannerImageHash = imageData.hash || null;
        } else if (imageData.type === BannerImageType.BANNER) {
            imageCreativeHash = imageData.hash || null;
        } else {
            creativeId = imageData.id;
        }
    }

    return {
        textBannerImageHash,
        imageCreativeHash,
        creativeId
    };
};

export const mapEditedBannerToPreviewData = memoize(
    (editedBanner: EditedBanner) => editedBanner,
    (editedBanner: EditedBanner, callouts: Callout[]) => callouts,
    (
        editedBanner: EditedBanner,
        callouts: Callout[],
        bannerImage: EditedBannerImage
    ) => bannerImage,
    (
        editedBanner: EditedBanner,
        callouts: Callout[],
        bannerImage: EditedBannerImage,
        video: EditedBannerVideo
    ) => video,
    (
        editedBanner: EditedBanner,
        callouts: Callout[],
        bannerImage: EditedBannerImage,
        video: EditedBannerVideo,
        turbolandings: EditedBannerTurbolanding
    ) => turbolandings,
    (
        editedBanner: EditedBanner,
        callouts: Callout[],
        bannerImage: EditedBannerImage,
        video: EditedBannerVideo,
        turbolandings: EditedBannerTurbolanding,
        organization: OrganizationData
    ) => organization,
    (
        editedBanner: EditedBanner,
        callouts: Callout[],
        bannerImage: EditedBannerImage,
        video?: EditedBannerVideo,
        turbolandings?: EditedBannerTurbolanding,
        organization?: OrganizationData
    ): PreviewData => {
        const { bannerType, id, status, isNew } = editedBanner;
        const titleBanner = getEditedBannerWithTitleAndBody(editedBanner);
        const contactsBanner = getEditedBannerWithFullContacts(editedBanner);
        const imageBanner = getEditedBannerWithImage(editedBanner);
        const sitelinksBanner = getEditedBannerWithSitelinks(editedBanner);
        const vCardBanner = getEditedBannerWithVcard(editedBanner);
        const calloutsBanner = getEditedBannerWithCallouts(editedBanner);

        let previewDomain = i18n('banner-default-domain');
        const protocol = contactsBanner
            ? contactsBanner.contacts.hrefProtocol
            : '';
        const { href, hrefMobile } = contactsBanner
            ? getHrefForBanner(contactsBanner, turbolandings)
            : { href: '', hrefMobile: '' };
        const domain = contactsBanner ? contactsBanner.contacts.domain : '';
        const displayHref = contactsBanner
            ? contactsBanner.contacts.displayHref
            : '';
        if (domain.length) {
            previewDomain = domain;
        }

        const calloutsList: string[] = [];

        if (calloutsBanner) {
            calloutsBanner.callouts.selected.forEach(selectedId => {
                const callout = callouts.find(c => c.id === selectedId);
                if (callout !== undefined) {
                    calloutsList.push(callout.text);
                }
            });
        }

        let vCardData: {
            vcardUrl: string;
            workingTime: string;
            phone: string;
            subwayStation?: SubwayStationInfoForPreviewData;
            region: string;
        };

        if (vCardBanner && vCardBanner.vCardId) {
            const vCardInfo = vCardBanner.vCard.info;

            vCardData = {
                vcardUrl: UrlHelpers.getVCardForPreviewDataUrl(
                    editedBanner.campaign.id,
                    id
                ),
                workingTime:
                    (vCardInfo &&
                        mapWorkingPeriodsToSerpPreviewData(
                            vCardInfo.periods
                        )) ||
                    '',
                phone:
                    (vCardInfo && mapPhoneToSerpPreviewData(vCardInfo.phone)) ||
                    '',
                region: (vCardInfo && vCardInfo.address.city) || '',
                subwayStation:
                    vCardInfo &&
                    vCardInfo.metroStationId &&
                    !vCardBanner.vCard.isNotShowMetroInSearchChecked
                        ? {
                              id: vCardInfo.metroStationId || '',
                              name: {
                                  ru: {
                                      genitive:
                                          vCardInfo.metroStationName || '',
                                      locative: '',
                                      nominative:
                                          vCardInfo.metroStationName || ''
                                  }
                              }
                          }
                        : undefined
            };
        } else {
            vCardData = {
                vcardUrl: '',
                workingTime: '',
                phone: '',
                region: ''
            };
        }

        if (organization) {
            const { phone, workTimes, address, profileUrl } = organization;

            vCardData = {
                vcardUrl: profileUrl,
                workingTime: workTimes
                    ? mapWorkingPeriodsToSerpPreviewData(workTimes)
                    : '',
                phone: phone ? mapPhoneToSerpPreviewData(phone) : '',
                region: address && address.city ? address.city : ''
            };
        }

        return {
            id,
            isNew,
            bannerType,
            title:
                (titleBanner && replaceTemplate(titleBanner.title)) ||
                i18n('banner-default-title'),
            titleExtension:
                (titleBanner && replaceTemplate(titleBanner.titleExtension)) ||
                '',
            body:
                (titleBanner && replaceTemplate(titleBanner.body)) ||
                i18n('banner-default-body'),
            domain: previewDomain,
            displayHref,
            href,
            hrefMobile,
            protocol,
            status,
            image: imageBanner && bannerImage.data,
            video: video && {
                url: video.data ? video.data.previewUrl : '',
                name: video.data ? video.data.name : ''
            },
            linkTail: titleBanner && titleBanner.linkTail,
            dynamicDisclaimer: titleBanner && titleBanner.dynamicDisclaimer,
            sitelinks:
                (sitelinksBanner && sitelinksBanner.sitelinks.items) || [],
            callouts: calloutsList,
            age: titleBanner && titleBanner.age ? `${titleBanner.age}+` : '',
            warning: (titleBanner && titleBanner.warning) || '',
            ...vCardData
        };
    }
)(
    (
        editedBanner: EditedBanner,
        callouts: Callout[],
        bannerImage: EditedBannerImage,
        video: EditedBannerVideo,
        turbolandings: EditedBannerTurbolanding,
        organization: OrganizationData,
        bannerId: BannerId
    ) => bannerId
);

export const mapVCardToApiFormat = (
    vCard: EditedBannerVCard,
    campaignId: string
): GdAddVcardInput => {
    const info = vCard.info!;

    return {
        campaignId,
        ...mapVCardInfoToApiFormat(info, vCard.isNotShowMetroInSearchChecked)
    };
};

export const mapVCardInfoToApiFormat = (
    info: VcardData,
    isNotShowMetroInSearchChecked: boolean
): Omit<GdAddVcardInput, 'campaignId'> => {
    return {
        instantMessenger:
            info.messengerType !== MessengerType.DEFAULT
                ? {
                      login: info.messengerPhone,
                      type: convertMessengerTypeToApiFormat(info.messengerType)
                  }
                : null,
        workTimes: info.periods
            .filter(period => period.week.length !== 0)
            .map(period => {
                return {
                    daysOfWeek: period.week,
                    startTime: {
                        hour: parseNumber(period.time.start.split(':')[0]),
                        minute: parseNumber(period.time.start.split(':')[1])
                    },
                    endTime: {
                        hour:
                            period.time.end !== '24:00'
                                ? parseNumber(period.time.end.split(':')[0])
                                : 0,
                        minute:
                            period.time.end !== '24:00'
                                ? parseNumber(period.time.end.split(':')[1])
                                : 0
                    }
                };
            }),
        companyName: info.name,
        contactPerson: info.contactPerson || null,
        ogrn: info.ogrn !== '' ? info.ogrn : null,
        email: info.mail !== '' ? info.mail : null,
        phone: {
            phoneNumber: info.phone.phoneNumber,
            extension: info.phone.extension,
            cityCode: info.phone.codeRegion,
            countryCode: info.phone.codeCountry
        },
        address: {
            country:
                info.address.country !== undefined ? info.address.country! : '',
            metroStationId:
                info.metroStationId !== '0' &&
                info.metroStationId !== null &&
                !isNotShowMetroInSearchChecked
                    ? parseNumber(info.metroStationId!)
                    : null,
            city: info.address.city !== undefined ? info.address.city : '',
            pointOnMap:
                info.pointOnMap !== null
                    ? {
                          x: info.pointOnMap.y.toString(),
                          y: info.pointOnMap.x.toString(),
                          x1: info.pointOnMap.y.toString(),
                          y1: info.pointOnMap.x.toString(),
                          x2: info.pointOnMap.y.toString(),
                          y2: info.pointOnMap.x.toString()
                      }
                    : null,
            apartment:
                info.office !== undefined && info.office !== ''
                    ? info.office
                    : null,
            houseWithBuilding:
                info.address.house !== undefined && info.address.house !== ''
                    ? info.address.house
                    : null,
            street:
                info.address.street !== undefined ? info.address.street : null
        },
        extraMessage: info.descr !== '' ? info.descr : null
    };
};

export const convertMessengerTypeToApiFormat = (
    type: MessengerType
): GdInstantMessengerTypeInput => {
    switch (type) {
        case MessengerType.MAIL_AGENT:
            return GdInstantMessengerTypeInput.MAIL_AGENT;
        case MessengerType.SKYPE:
            return GdInstantMessengerTypeInput.SKYPE;
        case MessengerType.JABBER:
            return GdInstantMessengerTypeInput.JABBER;
        case MessengerType.ICQ:
            return GdInstantMessengerTypeInput.ICQ;
        default:
            throw Error(`unsupported MessengerType ${type}`);
    }
};

const getBannerStub = (
    bannerType: AdvType
): Omit<GdAddAdInput, 'adGroupId'> | Omit<GdUpdateAdInput, 'id'> => ({
    isMobile: false,
    title: '',
    titleExtension: null,
    body: '',
    vcardId: null,
    adType: mapStringToGdAdvTypeInput(bannerType),
    textBannerImageHash: null,
    imageCreativeHash: null,
    creativeId: null,
    calloutIds: null,
    href: null,
    displayHref: null,
    domain: null
});

export const mapBannerDataToApiAddFormat = (
    banner: EditedBanner,
    commonData: CommonData,
    imageEditor: ImageEditorState
): Omit<GdAddAdInput, 'adGroupId'> => {
    const accessRights = commonData.accessRights || {};
    switch (banner.bannerType) {
        case AdvType.TEXT:
        case AdvTypeClient.TEXT_MOBILE:
            return mapBannerDataToApiAddFormatTypeText(
                banner,
                accessRights,
                imageEditor
            );
        case AdvType.IMAGE_AD:
            return mapBannerDataToApiFormatTypeImage(
                banner,
                accessRights,
                imageEditor
            );
        case AdvType.CPC_VIDEO:
            return mapBannerDataToApiFormatTypeCpcVideo(banner, accessRights);
        case AdvType.DYNAMIC:
        case AdvType.MOBILE_CONTENT:
        case AdvType.PERFORMANCE:
        case AdvType.MCBANNER:
        default:
            return getBannerStub(banner.bannerType);
    }
};

export const mapBannerDataToApiEditFormat = (
    banner: EditedBanner,
    commonData: CommonData,
    imageEditor: ImageEditorState
): Omit<GdUpdateAdInput, 'id'> => {
    const accessRights = commonData.accessRights || {};
    switch (banner.bannerType) {
        case AdvType.TEXT:
        case AdvTypeClient.TEXT_MOBILE:
            return mapBannerDataToApiEditFormatTypeText(
                banner,
                accessRights,
                imageEditor
            );
        case AdvType.IMAGE_AD:
            return mapBannerDataToApiFormatTypeImage(
                banner,
                accessRights,
                imageEditor
            );
        case AdvType.CPC_VIDEO:
            return mapBannerDataToApiFormatTypeCpcVideo(banner, accessRights);
        case AdvType.DYNAMIC:
        case AdvType.MOBILE_CONTENT:
        case AdvType.PERFORMANCE:
        case AdvType.MCBANNER:
        default:
            return getBannerStub(banner.bannerType);
    }
};

type ContactsApiFormatInfo = {
    href: string | undefined;
    displayHref?: string | null;
    vcardId?: string | null;
    turbolandingId?: string;
    turbolandingHrefParams?: string;
};

export const mapContactsInfoToApiFormat = (
    banner:
        | EditedBannerTypeText
        | EditedBannerTypeImage
        | EditedBannerTypeCpcVideo
        | EditedBannerTypeCpmBanner
        | EditedBannerTypeCpmAudio
        | EditedBannerTypeCpmVideo
        | EditedBannerTypeCpmGeoproduct
        | EditedBannerTypeCpmOutdoor
        | EditedBannerTypeCpmIndoor
        | EditedBannerTypeMcBanner
): ContactsApiFormatInfo => {
    if (banner.bannerType === AdvType.CPM_GEOPRODUCT) {
        const turboMetro = mapTurbolandingDataToAddApiFormat(
            banner.contacts.turbolanding
        );

        return {
            href: undefined,
            ...turboMetro
        };
    }

    const {
        desktopAndTabletType,
        mobileType,
        href,
        hrefProtocol,
        displayHref
    } = banner.contacts;
    let newHref: string | undefined = undefined;
    let newDisplayHref: string | null = null;
    let turbo: TurbolandingApiFormat = undefined;
    let vcardId: string | null = null;

    if (
        desktopAndTabletType === ContactsEditorType.TURBO ||
        mobileType === ContactsEditorType.TURBO
    ) {
        turbo = mapTurbolandingDataToAddApiFormat(banner.contacts.turbolanding);
    }

    if (desktopAndTabletType === ContactsEditorType.LINK && href) {
        newHref = hrefProtocol + href;
    }

    if (desktopAndTabletType === ContactsEditorType.LINK && displayHref) {
        newDisplayHref = displayHref;
    }

    const bannerWithVCard = getEditedBannerWithVcard(banner);

    if (bannerWithVCard && bannerWithVCard.vCardId) {
        vcardId = bannerWithVCard.vCardId;
    }

    const baseContactsData = {
        href: newHref,
        ...turbo
    };

    if (
        Array<AdvType | AdvTypeClient>(
            AdvType.CPM_BANNER,
            AdvType.CPM_VIDEO,
            AdvType.CPM_INDOOR,
            AdvType.CPM_OUTDOOR,
            AdvType.CPM_AUDIO
        ).includes(banner.bannerType)
    ) {
        return {
            ...baseContactsData,
            ...turbo
        };
    }

    if (banner.bannerType === AdvType.MCBANNER) {
        return baseContactsData;
    }

    return {
        ...baseContactsData,
        displayHref: newDisplayHref,
        vcardId
    };
};

export const mapBannerDataToApiAddFormatTypeText = (
    banner: EditedBannerTypeText,
    accessRights: AccessRights,
    imageEditor: ImageEditorState
): Omit<GdAddAdInput, 'adGroupId'> => {
    const turboGalleryHref =
        banner.turboGallery.href.length > 0 ? banner.turboGallery.href : null;

    return {
        turboGalleryHref,
        ...mapBannerDataToApiFormatTypeText(banner, accessRights, imageEditor)
    };
};

export const mapBannerDataToApiEditFormatTypeText = (
    banner: EditedBannerTypeText,
    accessRights: AccessRights,
    imageEditor: ImageEditorState
): Omit<GdUpdateAdInput, 'id'> => {
    const turboGalleryHref =
        banner.turboGallery.href.length > 0 ? banner.turboGallery.href : null;

    return {
        turboGalleryParams: { turboGalleryHref },
        ...mapBannerDataToApiFormatTypeText(banner, accessRights, imageEditor)
    };
};

export const mapBannerDataToApiFormatTypeText = (
    banner: EditedBannerTypeText,
    accessRights: AccessRights,
    imageEditor: ImageEditorState
): Omit<GdAddAdInput, 'adGroupId'> | GdUpdateAdInput => {
    const {
        textBannerImageHash,
        imageCreativeHash,
        creativeId
    } = mapImageDataToApiFormat(imageEditor.image);
    const bannerType = banner.bannerType;
    let isMobile = false;

    if (bannerType === AdvTypeClient.TEXT_MOBILE) {
        isMobile = true;
    }

    return {
        isMobile,
        title: banner.title,
        titleExtension: banner.titleExtension || null,
        body: banner.body,
        ...mapContactsInfoToApiFormat(banner),
        adType: mapStringToGdAdvTypeInput(AdvType.TEXT),
        textBannerImageHash: textBannerImageHash,
        imageCreativeHash: imageCreativeHash,
        calloutIds: banner.callouts.selected,
        domain:
            accessRights.canEditAdDomain &&
            banner.contacts.href.length > 0 &&
            banner.contacts.domain.length > 0
                ? banner.contacts.domain
                : null,
        creativeId:
            (banner.video && banner.video.data && banner.video.data.id) ||
            creativeId ||
            null,
        sitelinksSetId: banner.sitelinksSetId,
        adPrice: mapBannersPriceDataToApiFormat(banner),
        permalinkId: banner.permalinkId
    };
};

export const mapBannerDataToApiFormatTypeImage = (
    banner: EditedBannerTypeImage,
    accessRights: AccessRights,
    imageEditor: ImageEditorState
): Omit<GdAddAdInput, 'adGroupId'> => {
    const image = mapImageDataToApiFormat(imageEditor.image);

    return {
        isMobile: false,
        title: '',
        titleExtension: null,
        body: '',
        adType: mapStringToGdAdvTypeInput(AdvType.IMAGE_AD),
        ...image,
        calloutIds: null,
        ...mapContactsInfoToApiFormat(banner),
        domain:
            accessRights.canEditAdDomain &&
            banner.contacts.href &&
            banner.contacts.domain.length > 0
                ? banner.contacts.domain
                : null,
        sitelinksSetId: null
    };
};

export const mapBannerDataToApiFormatTypeCpcVideo = (
    banner: EditedBannerTypeCpcVideo,
    accessRights: AccessRights
): Omit<GdAddAdInput, 'adGroupId'> => {
    return {
        isMobile: false,
        title: '',
        titleExtension: null,
        body: '',
        adType: mapStringToGdAdvTypeInput(AdvType.CPC_VIDEO),
        calloutIds: null,
        domain:
            accessRights.canEditAdDomain &&
            banner.contacts.href &&
            banner.contacts.domain.length > 0
                ? banner.contacts.domain
                : null,
        creativeId:
            (banner.video && banner.video.data && banner.video.data.id) || null,
        ...mapContactsInfoToApiFormat(banner),
        sitelinksSetId: null
    };
};

export const mapBannerDataToApiFormatTypeCpmBanner = (
    banner: EditedCpmBanner
): Omit<GdAddCpmAdInput, 'adGroupId'> => {
    const creativeId = Array<AdvType | AdvTypeClient>(
        AdvType.CPM_OUTDOOR,
        AdvType.CPM_VIDEO,
        AdvType.CPM_INDOOR,
        AdvType.CPM_AUDIO
    ).includes(banner.bannerType)
        ? get(banner, 'video.data.id', null)
        : get(banner, 'creative.data.id', null);

    const pixels = mapPixelsToApiFormat(banner.pixel, banner.showsCounters);
    const measurers = mapMeasurersToApiFormat(banner.measurers);

    const {
        href,
        turbolandingId,
        turbolandingHrefParams
    } = mapContactsInfoToApiFormat(banner);

    const optionalFields: {
        tnsId?: string;
    } = {};

    if (
        banner.bannerType === AdvType.CPM_BANNER ||
        banner.bannerType === AdvType.CPM_VIDEO
    ) {
        if (banner.tnsId) {
            optionalFields.tnsId = banner.tnsId;
        }
    }

    return {
        adType: mapStringToGdAdvTypeInput(banner.bannerType),
        creativeId: creativeId || null,
        href,
        turbolandingId,
        turbolandingHrefParams: turbolandingHrefParams || '',
        pixels: pixels.length ? pixels : [],
        measurers: measurers.length ? measurers : [],
        ...optionalFields
    };
};

export const mapBannerDataToApiFormatTypeMcBanner = (
    banner: EditedBannerTypeMcBanner,
    imageEditor: ImageEditorState
): Omit<GdAddMcBannerAdInput, 'adGroupId'> => {
    const image = imageEditor.image;

    let hash;

    if (image) {
        const data = (image.data || {}) as BannerImageForEdit;
        hash = data.hash;
    }

    const { href } = mapContactsInfoToApiFormat(banner);

    return {
        href: href || '',
        adType: mapStringToGdAdvTypeInput(banner.bannerType),
        adImageHash: hash || ''
    };
};

export const mapBannersToApiAddFormat = (
    banners: Record<BannerId, EditedBanner>,
    commonData: CommonData,
    imageEditor: ImageEditorsState
): GdAddAdsInput => {
    return {
        saveDraft: false,
        adAddItems: Object.keys(banners).map(
            (bannerId: BannerId): GdAddAdInput => {
                const banner = banners[bannerId];

                return {
                    ...mapBannerDataToApiAddFormat(
                        banner,
                        commonData,
                        imageEditor[bannerId]
                    ),
                    adGroupId: banner.group.id
                };
            }
        )
    };
};

export const mapBannersToApiDeleteFormat = <T extends EditedBannerBase>(
    banners: Record<BannerId, T>
): GdAdsMassActionInput => {
    return {
        adIds: Object.keys(banners)
    };
};

export const mapBannersToApiEditFormat = (
    banners: Record<BannerId, EditedBanner>,
    commonData: CommonData,
    imageEditor: ImageEditorsState
): GdUpdateAdsInput => {
    return {
        saveDraft: false,
        adUpdateItems: Object.keys(banners)
            .filter(
                bannerId =>
                    !banners[bannerId].isSaved &&
                    [
                        AdvType.TEXT,
                        AdvType.IMAGE_AD,
                        AdvTypeClient.TEXT_MOBILE,
                        AdvType.CPC_VIDEO
                    ].includes(banners[bannerId].bannerType) &&
                    banners[bannerId].isEditAllow
            )
            .map(
                (bannerId: BannerId): GdUpdateAdInput => {
                    const banner = banners[bannerId];

                    return {
                        ...mapBannerDataToApiEditFormat(
                            banner,
                            commonData,
                            imageEditor[bannerId]
                        ),
                        id: banner.id
                    };
                }
            )
    };
};

export const mapCpmBannersToApiAddFormat = (
    banners: Record<BannerId, EditedCpmBanner>
): GdAddCpmAdsInput => {
    return {
        saveDraft: false,
        adAddItems: Object.keys(banners).map(
            (bannerId: BannerId): GdAddCpmAdInput => {
                const banner = banners[bannerId];

                return {
                    ...mapBannerDataToApiFormatTypeCpmBanner(banner),
                    adGroupId: banner.group.id
                };
            }
        )
    };
};

export const mapCpmBannersToApiEditFormat = (
    banners: Record<BannerId, EditedCpmBanner>
): GdUpdateCpmAdsInput => {
    return {
        saveDraft: false,
        adUpdateItems: Object.keys(banners)
            .filter(
                bannerId =>
                    !banners[bannerId].isSaved && banners[bannerId].isEditAllow
            )
            .map(
                (bannerId: BannerId): GdUpdateCpmAdInput => {
                    const banner = banners[bannerId];

                    return {
                        ...mapBannerDataToApiFormatTypeCpmBanner(banner),
                        id: banner.id
                    };
                }
            )
    };
};

export const mapMcBannerBannersToApiAddFormat = (
    banners: Record<BannerId, EditedBannerTypeMcBanner>,
    imageEditor: ImageEditorsState
): GdAddMcBannerAdsInput => {
    return {
        saveDraft: false,
        adAddItems: Object.keys(banners).map(
            (bannerId: BannerId): GdAddMcBannerAdInput => {
                const banner = banners[bannerId];

                return {
                    ...mapBannerDataToApiFormatTypeMcBanner(
                        banner,
                        imageEditor[bannerId]
                    ),
                    adGroupId: banner.group.id
                };
            }
        )
    };
};

export const mapMcBannerBannersToApiEditFormat = (
    banners: Record<BannerId, EditedBannerTypeMcBanner>,
    imageEditor: ImageEditorsState
): GdUpdateMcBannerAdsInput => {
    return {
        saveDraft: false,
        adUpdateItems: Object.keys(banners).map(
            (bannerId: BannerId): GdUpdateMcBannerAdInput => {
                const banner = banners[bannerId];

                return {
                    ...mapBannerDataToApiFormatTypeMcBanner(
                        banner,
                        imageEditor[bannerId]
                    ),
                    id: banner.id
                };
            }
        )
    };
};

export const mapInternalBannerEditDataToApiFormat = (
    banner: EditedInternalBanner
): GdUpdateInternalAdsItemInput => {
    return {
        id: banner.id,
        description: banner.description,
        templateVars: banner.templateVars
            .filter(templateVar => {
                return templateVar.templateId === banner.templateId;
            })
            .map(templateVar => {
                if (templateVar.value === '') {
                    templateVar.value = null;
                }

                return {
                    value: templateVar.value,
                    templateResourceId: templateVar.resourceId
                };
            })
    };
};

export const mapInternalBannerAddDataToApiFormat = (
    banner: EditedInternalBanner
): GdAddInternalAdsItemInput => {
    return {
        adGroupId: banner.group.id,
        description: banner.description,
        templateId: banner.templateId,
        templateVars: banner.templateVars
            .filter(templateVar => {
                return templateVar.templateId === banner.templateId;
            })
            .map(templateVar => {
                if (templateVar.value === '') {
                    templateVar.value = null;
                }

                return {
                    value: templateVar.value,
                    templateResourceId: templateVar.resourceId
                };
            })
    };
};

export const mapInternalBannersToApiEditFormat = (
    banners: Record<BannerId, EditedInternalBanner>
): GdUpdateInternalAdsInput => {
    return {
        saveDraft: false,
        adUpdateItems: Object.keys(banners)
            .filter(bannerId => !banners[bannerId].isSaved)
            .map(
                (bannerId: BannerId): GdUpdateInternalAdsItemInput => {
                    const banner = banners[bannerId];

                    return {
                        ...mapInternalBannerEditDataToApiFormat(banner),
                        id: banner.id
                    };
                }
            )
    };
};

export const mapInternalBannersToApiAddFormat = (
    banners: Record<BannerId, EditedInternalBanner>
): GdAddInternalAdsInput => {
    return {
        saveDraft: false,
        adAddItems: Object.keys(banners)
            .filter(bannerId => !banners[bannerId].isSaved)
            .map(
                (bannerId: BannerId): GdAddInternalAdsItemInput => {
                    const banner = banners[bannerId];

                    return mapInternalBannerAddDataToApiFormat(banner);
                }
            )
    };
};

export const mapSmartBannersToApiAddFormat = (
    banners: Record<BannerId, EditedBannerTypePerformance>
): GdAddSmartAdsInput => {
    return {
        saveDraft: false,
        adAddItems: Object.keys(banners).map(bannerId => ({
            adGroupId: banners[bannerId].group.id,
            creativeId: banners[bannerId].creative.id
        }))
    };
};

export const mapSmartBannersToApiEditFormat = (
    banners: Record<BannerId, EditedBannerTypePerformance>
): GdUpdateSmartAdsInput => {
    return {
        saveDraft: false,
        adUpdateItems: Object.keys(banners).map(bannerId => ({
            id: banners[bannerId].id,
            creativeId: banners[bannerId].creative.id
        }))
    };
};

export const mapBannerImageTypeForGalleryItems = (
    bannerType: AdvType | AdvTypeClient
): BannerImageType => {
    switch (bannerType) {
        case AdvType.TEXT:
        case AdvTypeClient.TEXT_MOBILE:
            return BannerImageType.TEXT;
        case AdvType.IMAGE_AD:
        case AdvType.CPM_BANNER:
        case AdvType.CPM_VIDEO:
        case AdvType.MCBANNER:
            return BannerImageType.BANNER;
        default:
            return BannerImageType.TEXT;
    }
};

export const mapBannersPriceDataToApiFormat = (
    banner: EditedBanner
): GdAdPriceInput | null => {
    const bannerPrice = get(banner, 'bannerPriceEditor.bannerPrice');

    return bannerPrice ? getBannerPriceDataToApi(bannerPrice) : null;
};

export const getHrefForBanner = (
    banner: EditedBannerBase & { contacts: EditedBannerFullContacts },
    turbolanding?: EditedBannerTurbolanding
): {
    href: string;
    hrefMobile: string;
} => {
    // TODO Доделать до отображения VCARD (если это нужно?)
    const { TURBO } = ContactsEditorType;

    const normalHref = banner.contacts.hrefProtocol + banner.contacts.href;

    if (!turbolanding || !turbolanding.page) {
        return { href: normalHref, hrefMobile: normalHref };
    }

    return {
        href:
            banner.contacts.desktopAndTabletType === TURBO
                ? turbolanding.page.href
                : normalHref,
        hrefMobile:
            banner.contacts.mobileType === TURBO
                ? turbolanding.page.href
                : normalHref
    };
};

export const mapVisitingCardEditorValueToValue = (
    editorValue: VisitingCardEditorState
): EditedBannerVCard => {
    const {
        isShortVersionChecked,
        isNotShowMetroInSearchChecked,
        vCardData
    } = editorValue;

    return {
        isShortVersionChecked,
        isNotShowMetroInSearchChecked,
        info: vCardData
            ? {
                  ...vCardData
              }
            : undefined
    };
};

export const mapVisitingCardErrors = (
    errorsData: Record<string, ValidationResultItem[]>
): Record<string, ValidationErrorData[]> => {
    return Object.keys(errorsData).reduce((acc, key) => {
        acc[key] = errorsData[key].map(
            (error: ValidationResultItem): ValidationErrorData => {
                return {
                    path: error.path,
                    code: error.code,
                    text: VCardErrorMapper({
                        ...error
                    } as ValidationResultItem)
                };
            }
        );

        return acc;
    }, {});
};
